import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import theme from "../../theme";
import CommonInput from "../input";
import CommonButton from "../button";
import CommonFormDropDown from "../formdropdown";

const useStyles = makeStyles(() => ({
  root: {
    padding: "50px",
    "& .box": {
      background: theme.colors.White,
      boxShadow: "0px 2px 15px #D0CFDB99",
      borderRadius: "15px",
      padding: "30px 60px",
      "& .block": {
        marginBottom: "30px",
        "& .title": {
          fontSize: "21px",
          fontFamily: theme.fontFamily.MARKPROBOLD,
          color: theme.colors.DodgerBlue,
          marginBottom: "15px",
        },
        "& .flex": {
          display: "flex",
          alignItems: "center",
          "& .label": {
            flex: 0.3,
            fontSize: "16px",
            fontFamily: theme.fontFamily.MARKPROMEDIUM,
            marginBottom: "20px",
          },
          "& .field": { flex: 0.9 },
        },
      },
      "& .buttonContainer": { display: "flex", justifyContent: "center", gap: "15px" },
      "& .logoSection": {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      },
      "& .logoPreviewContainer": {
        display: "flex",
        alignItems: "center",
        gap: "15px",
      },
      "& .logoPreview": {
        width: "100px",
        height: "100px",
        objectFit: "cover",
        borderRadius: "8px",
        border: `1px solid ${theme.colors.Zumthor}`,
      },
      "& .removeLogoButton": {
        cursor: "pointer",
        color: theme.colors.Monza,
        fontSize: "12px",
        fontFamily: theme.fontFamily.MARKPRO,
        textDecoration: "underline",
        background: "none",
        border: "none",
        padding: 0,
      },
    },
  },
}));

const settlementPeriodOptions = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "DAILY", label: "Daily" },
  { value: "MONTHLY", label: "Monthly" },
];

function EditMerchantDetail({
  editMerchantDetail,
  setEditMerchantDetail,
  updateMerchantMethod,
}) {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const dashboardReducer = useSelector((state) => state.dashboardReducer);

  const [isValidated, setIsValidated] = useState(false);
  const [businessLogo, setBusinessLogo] = useState(null);
  const [businessLogoPreview, setBusinessLogoPreview] = useState(
    editMerchantDetail?.business_logo || ""
  );

  const [businessName, setBusinessName] = useState(editMerchantDetail?.business_name || "");
  const [businessDescription, setBusinessDescription] = useState(
    editMerchantDetail?.business_description || ""
  );
  const [address, setAddress] = useState(editMerchantDetail?.address || "");
  const [phoneNumber, setPhoneNumber] = useState(editMerchantDetail?.phone_number || "");
  const [startTime, setStartTime] = useState(editMerchantDetail?.start_time || "");
  const [endTime, setEndTime] = useState(editMerchantDetail?.end_time || "");
  const [settlementChargePercentage, setSettlementChargePercentage] = useState(
    editMerchantDetail?.settlement_charge_percentage?.toString() || "0.00"
  );
  const [settlementPeriodType, setSettlementPeriodType] = useState(
    editMerchantDetail?.settlement_period_type
      ? {
        value: editMerchantDetail.settlement_period_type,
        label:
          editMerchantDetail.settlement_period_type === "WEEKLY"
            ? "Weekly"
            : editMerchantDetail.settlement_period_type === "DAILY"
              ? "Daily"
              : editMerchantDetail.settlement_period_type === "MONTHLY"
                ? "Monthly"
                : "Custom",
      }
      : null
  );

  useEffect(() => {
    if (dashboardReducer?.updateMerchantSuccess) {
      setEditMerchantDetail(null);
    }
  }, [dashboardReducer, setEditMerchantDetail]);

  useEffect(() => {
    if (dashboardReducer?.error?.length) {
      const ele = document.getElementsByClassName("error-container");
      if (ele && ele.length) ele[0].scrollIntoView(false);
    }
  }, [dashboardReducer]);

  const validateTimeFormat = (time) => {
    if (!time) return true;
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Basic size guard — reject files over 5MB
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be smaller than 5MB.");
      return;
    }

    setBusinessLogo(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setBusinessLogoPreview(reader.result);
    };
  };

  const handleRemoveLogo = () => {
    setBusinessLogo(null);
    setBusinessLogoPreview(editMerchantDetail?.business_logo || "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsValidated(true);

    const isValid =
      businessName?.trim()?.length > 0 &&
      address?.trim()?.length > 0 &&
      phoneNumber?.trim()?.length > 0 &&
      validateTimeFormat(startTime) &&
      validateTimeFormat(endTime) &&
      settlementChargePercentage?.trim() !== "" &&
      !isNaN(parseFloat(settlementChargePercentage)) &&
      settlementPeriodType;

    if (!isValid) return;

    const payload = {
      business_name: businessName.trim(),
      business_description: businessDescription.trim() || "",
      address: address.trim(),
      phone_number: phoneNumber.trim(),
      start_time: startTime.trim() || null,
      end_time: endTime.trim() || null,
      settlement_charge_percentage: settlementChargePercentage.trim(),
      settlement_period_type: settlementPeriodType.value,
    };

    if (businessLogo instanceof File) {
      payload.business_logo = businessLogo;
    }

    updateMerchantMethod(payload, editMerchantDetail?.id);
  };

  return (
    <div className={classes.root}>
      {dashboardReducer?.updateMerchantError?.length ? (
        <div className="error-container">
          {Array.isArray(dashboardReducer.updateMerchantError)
            ? dashboardReducer.updateMerchantError.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : <div>{dashboardReducer.updateMerchantError}</div>}
        </div>
      ) : null}

      <div className="box">
        <div className="block">
          <div className="title">Edit Merchant Details</div>
          <form autoComplete="off" onSubmit={handleSubmit}>

            {/* Business Logo */}
            <div className="flex">
              <div className="label">Business Logo:</div>
              <div className="field">
                <div className="logoSection">
                  {/* Preview + remove — only shown when there is a preview URL */}
                  {(businessLogoPreview || editMerchantDetail?.business_logo) ? (
                    <div className="logoPreviewContainer">
                      <img
                        className="logoPreview"
                        src={businessLogoPreview || editMerchantDetail.business_logo}
                        alt="Business logo preview"
                      />
                      {businessLogo && (
                        <button
                          type="button"
                          className="removeLogoButton"
                          onClick={handleRemoveLogo}
                        >
                          Remove logo
                        </button>)
                      }
                    </div>
                  ) : null}

                  {/* Upload button — always visible */}
                  <CommonButton
                    stylevariant="secondary2"
                    type="button"
                    text={businessLogoPreview ? "Change Image" : "Upload Image"}
                    onClick={() => fileInputRef.current?.click()}
                    icon="/images/upload.png"
                  />

                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleLogoChange}
                    onClick={(e) => {
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Business Name */}
            <div className="flex">
              <div className="label">Business Name:</div>
              <div className="field">
                <CommonInput
                  placeholder="Enter business name"
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  error={isValidated && (!businessName || !businessName.trim())}
                  helperText={
                    isValidated && (!businessName || !businessName.trim())
                      ? "Business Name may not be blank."
                      : ""
                  }
                  fullWidth
                />
              </div>
            </div>

            {/* Business Description */}
            <div className="flex">
              <div className="label">Business Description:</div>
              <div className="field">
                <CommonInput
                  placeholder="Enter business description"
                  type="text"
                  multiline
                  rows={4}
                  value={businessDescription}
                  onChange={(e) => setBusinessDescription(e.target.value)}
                  fullWidth
                />
              </div>
            </div>

            {/* Address */}
            <div className="flex">
              <div className="label">Address:</div>
              <div className="field">
                <CommonInput
                  placeholder="Enter address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  error={isValidated && (!address || !address.trim())}
                  helperText={
                    isValidated && (!address || !address.trim())
                      ? "Address may not be blank."
                      : ""
                  }
                  fullWidth
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="flex">
              <div className="label">Phone Number:</div>
              <div className="field">
                <CommonInput
                  placeholder="Enter phone number"
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  error={isValidated && (!phoneNumber || !phoneNumber.trim())}
                  helperText={
                    isValidated && (!phoneNumber || !phoneNumber.trim())
                      ? "Phone Number may not be blank."
                      : ""
                  }
                  fullWidth
                />
              </div>
            </div>

            {/* Open Time */}
            <div className="flex">
              <div className="label">Open Time:</div>
              <div className="field">
                <CommonInput
                  placeholder="HH:MM:SS (e.g., 09:00:00)"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  error={isValidated && !validateTimeFormat(startTime)}
                  helperText={
                    isValidated && !validateTimeFormat(startTime)
                      ? "Please enter time in HH:MM:SS format (e.g., 09:00:00)."
                      : ""
                  }
                  fullWidth
                />
              </div>
            </div>

            {/* Close Time */}
            <div className="flex">
              <div className="label">Close Time:</div>
              <div className="field">
                <CommonInput
                  placeholder="HH:MM:SS (e.g., 17:30:00)"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  error={isValidated && !validateTimeFormat(endTime)}
                  helperText={
                    isValidated && !validateTimeFormat(endTime)
                      ? "Please enter time in HH:MM:SS format (e.g., 17:30:00)."
                      : ""
                  }
                  fullWidth
                />
              </div>
            </div>

            {/* Settlement Charge Percentage */}
            <div className="flex">
              <div className="label">Settlement Charge Percentage:</div>
              <div className="field">
                <CommonInput
                  placeholder="Enter settlement charge percentage (e.g., 0.00)"
                  type="text"
                  value={settlementChargePercentage}
                  onChange={(e) => setSettlementChargePercentage(e.target.value)}
                  error={
                    isValidated &&
                    (!settlementChargePercentage ||
                      settlementChargePercentage.trim() === "" ||
                      isNaN(parseFloat(settlementChargePercentage)))
                  }
                  helperText={
                    isValidated &&
                      (!settlementChargePercentage ||
                        settlementChargePercentage.trim() === "" ||
                        isNaN(parseFloat(settlementChargePercentage)))
                      ? "Settlement Charge Percentage must be a valid number."
                      : ""
                  }
                  fullWidth
                />
              </div>
            </div>

            {/* Settlement Period Type */}
            <div className="flex">
              <div className="label">Settlement Period Type:</div>
              <div className="field">
                <CommonFormDropDown
                  placeholder="Select settlement period type"
                  options={settlementPeriodOptions}
                  value={settlementPeriodType}
                  onChange={(val) => setSettlementPeriodType(val)}
                />
              </div>
            </div>

            <div className="buttonContainer">
              <CommonButton
                type="button"
                text="Cancel"
                stylevariant="secondary"
                onClick={() => setEditMerchantDetail(null)}
                style={{ padding: "10px 40px" }}
              />
              <CommonButton
                type="submit"
                text="Save Changes"
                stylevariant="primary"
                style={{ padding: "10px 40px" }}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditMerchantDetail;